/**
 * Web wiring for the first-party event tracker (epic-event-tracking-foundation.md §EV6).
 *
 *   track('tournament.viewed', { entities: { tournamentId }, surface: 'tournament_detail', dwellMs })
 *
 * - Queue persisted in localStorage (memory fallback when storage is blocked), session in
 *   sessionStorage with a 30-minute idle rotation.
 * - Flushes at 50 events, every 30 s, and on `visibilitychange: hidden` / `pagehide` with
 *   `fetch(..., { keepalive: true })` so a closing tab still delivers. NOT `sendBeacon`: it cannot
 *   carry the Authorization header, and anonymous-vs-account is the whole point.
 * - Fires in EVERY environment (unlike PostHog/GA, prod-only): QA verifies the pipeline on dev.
 * - Logged-out visitors are counted as anonymous reach; the collector stamps user_id from the token
 *   and never from the body.
 */
import {
  EventQueue,
  getOrCreateAnonId,
  newUuid,
  resolveSession,
  type QueueStorage,
  type SendResult,
} from './eventQueue';
import type {
  CollectorResponse,
  EventEnvelope,
  EventName,
  TrackOptions,
} from './types';

// A root route on the API domain, separate from the main API's /public/api/v1/* tree (espotz-iac events_route.tf).
export const EVENTS_PATH = '/events';
const FLUSH_INTERVAL_MS = 30_000;
/** fetch keepalive bodies are capped at 64 KB by browsers; 50 events ≈ 20 KB. */
const KEEPALIVE_BODY_LIMIT = 60 * 1024;

const isProd = import.meta.env.VITE_APP_ENV === 'production';

/** localStorage when it works, an in-memory map when it doesn't (private mode, blocked storage). */
function makeStorage(backing: Storage | null): QueueStorage {
  const memory = new Map<string, string>();
  const probe = (): Storage | null => {
    if (!backing) return null;
    try {
      backing.setItem('analytics.probe', '1');
      backing.removeItem('analytics.probe');
      return backing;
    } catch {
      return null;
    }
  };
  const store = probe();
  return {
    get: key => (store ? store.getItem(key) : (memory.get(key) ?? null)),
    set: (key, value) =>
      store ? store.setItem(key, value) : void memory.set(key, value),
    remove: key => (store ? store.removeItem(key) : void memory.delete(key)),
  };
}

const safeWindowStorage = (name: 'localStorage' | 'sessionStorage') => {
  try {
    return typeof window !== 'undefined' ? window[name] : null;
  } catch {
    return null;
  }
};

export const eventsBaseUrl = (): string =>
  (
    (import.meta.env.VITE_EVENTS_BASE_URL as string | undefined) ||
    (import.meta.env.VITE_API_BASE_URL as string) ||
    ''
  ).replace(/\/+$/, '');

/** Same source as the axios interceptor in services/api.ts — raw token, no "Bearer". */
const authHeader = (): Record<string, string> => {
  try {
    const token = localStorage.getItem('accessToken');
    return token ? { Authorization: token } : {};
  } catch {
    return {};
  }
};

export function classifyStatus(status: number): SendResult['kind'] {
  if (status >= 200 && status < 300) return 'accepted';
  if (status >= 400 && status < 500 && status !== 429) return 'client_error';
  return 'retry';
}

async function send(events: EventEnvelope[]): Promise<SendResult> {
  const body = JSON.stringify({ events });
  try {
    const response = await fetch(`${eventsBaseUrl()}${EVENTS_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body,
      // keepalive lets the request outlive a closing tab; browsers refuse it above ~64 KB.
      keepalive: body.length <= KEEPALIVE_BODY_LIMIT,
      credentials: 'omit',
    });
    const kind = classifyStatus(response.status);
    if (kind === 'accepted') {
      const parsed = (await response
        .json()
        .catch(() => null)) as CollectorResponse | null;
      return {
        kind,
        response: parsed ?? { accepted: events.length, rejected: [] },
      };
    }
    if (kind === 'client_error') {
      return {
        kind,
        status: response.status,
        body: await response.json().catch(() => undefined),
      };
    }
    return { kind: 'retry', status: response.status };
  } catch (error) {
    return { kind: 'retry', error };
  }
}

let queue: EventQueue | null = null;
let localStore: QueueStorage | null = null;
let sessionStore: QueueStorage | null = null;
let anonId = '';
let sessionId = '';
let flushTimer: ReturnType<typeof setInterval> | null = null;
let listenersInstalled = false;

const log = (message: string, data?: unknown) => {
  if (!isProd) console.log(message, data ?? '');
};

function getQueue(): EventQueue {
  if (!queue) {
    localStore = makeStorage(safeWindowStorage('localStorage'));
    sessionStore = makeStorage(safeWindowStorage('sessionStorage'));
    queue = new EventQueue({
      storage: localStore,
      send,
      onRejected: rejected =>
        console.warn('[events] collector rejected events', rejected),
      onClientError: (status, body) =>
        console.error('[events] batch dropped by collector', status, body),
      onError: (message, error) => console.warn(message, error),
      log,
    });
    anonId = getOrCreateAnonId(localStore, newUuid);
    touchSession();
  }
  return queue;
}

function touchSession(): void {
  if (!sessionStore) return;
  const session = resolveSession(sessionStore, newUuid, Date.now());
  sessionId = session.sessionId;
  // A session just began (first load, or back after the idle window): epic 21's engaged-openers
  // denominator. Queued before whatever event triggered the rotation.
  if (
    session.isNew &&
    queue &&
    queue.enqueue(envelopeFor('app.session_started', { entities: {} }))
  )
    void queue.flush();
}

/** Start timers and page-lifecycle listeners. Call once at app start (App.tsx). Idempotent. */
export function initEventTracker(): () => void {
  const q = getQueue();
  if (!flushTimer)
    flushTimer = setInterval(() => void q.flush(), FLUSH_INTERVAL_MS);

  const onHide = () => {
    if (document.visibilityState === 'hidden') void q.flush(true);
  };
  const onPageHide = () => void q.flush(true);
  const onShow = () => {
    if (document.visibilityState === 'visible') {
      touchSession();
      void q.drain();
    }
  };
  if (!listenersInstalled && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onHide);
    document.addEventListener('visibilitychange', onShow);
    window.addEventListener('pagehide', onPageHide);
    listenersInstalled = true;
  }
  void q.drain();

  return () => {
    document.removeEventListener('visibilitychange', onHide);
    document.removeEventListener('visibilitychange', onShow);
    window.removeEventListener('pagehide', onPageHide);
    listenersInstalled = false;
    if (flushTimer) {
      clearInterval(flushTimer);
      flushTimer = null;
    }
  };
}

function envelopeFor(name: EventName, options: TrackOptions): EventEnvelope {
  return {
    eventId: newUuid(),
    name,
    schemaVersion: 1,
    occurredAtMs: Date.now(),
    anonId,
    sessionId,
    platform: 'web',
    // CI sets VITE_APP_VERSION to the full 40-char commit SHA; the short SHA is enough to identify a build.
    appVersion:
      (import.meta.env.VITE_APP_VERSION as string | undefined)?.slice(0, 12) ||
      undefined,
    entities: options.entities,
    ...(options.surface ? { surface: options.surface } : {}),
    ...(options.position !== undefined ? { position: options.position } : {}),
    ...(options.dwellMs !== undefined
      ? { dwellMs: Math.round(options.dwellMs) }
      : {}),
    ...(options.metadata ? { metadata: options.metadata } : {}),
  };
}

/** Record one event. Synchronous; persistence happens before this returns. */
export function track(name: EventName, options: TrackOptions): void {
  try {
    const q = getQueue();
    touchSession();
    const envelope = envelopeFor(name, options);
    log('📡 event', {
      name,
      entities: envelope.entities,
      dwellMs: options.dwellMs,
    });
    if (q.enqueue(envelope)) void q.flush();
  } catch (error) {
    console.warn('[events] track failed', name, error);
  }
}

export const currentSessionId = (): string => {
  getQueue();
  return sessionId;
};

/**
 * The persisted per-install id the events carry. The feed sends it so a logged-out viewer can be
 * frequency-capped for brand ads; it is not personal data.
 */
export const currentAnonId = (): string => {
  getQueue();
  return anonId;
};

export const flushEvents = (force = false): Promise<void> =>
  getQueue().flush(force);

/** Test hook. */
export function resetEventTrackerForTests(): void {
  queue = null;
  localStore = null;
  sessionStore = null;
  anonId = '';
  sessionId = '';
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  listenersInstalled = false;
}
